import { MODAL_NAMES } from '@/vue/modals/MODAL_NAMES';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { MODAL_NAMES__KITCHEN } from '@/kitchen/vue/modals/MODAL_NAMES__KITCHEN';
import { MODAL_NAMES__KITCHEN_CUSTOMER } from '@/kitchen-customer/vue/modals/MODAL_NAMES__KITCHEN_CUSTOMER';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

/**
 * @typedef {object} ModalOptions
 * @type {Function} [onOpen] - Will receive the ID of the modal when it is created. This can be used to
 *                             programmatically close the modal without user interaction.
 */

export default class VueModalFactory {
    /**
     * Open a modal or sheet by giving the name of a Vue component to render.
     * The functionality of a modal and sheet is essentially the same the difference is in how they are displayed.
     * The given object of props will be passed to the component.
     *
     * @param {string} componentName
     * @param {object} [props]
     * @param {ModalOptions} [options]
     *
     * @returns {Promise}
     */
    open(componentName, props = {}, options = {}) {
        // FIXME: For multiple modal sources.
        if (
            Object.values(MODAL_NAMES).indexOf(componentName) === -1
            && Object.values(MODAL_NAMES__EPOS).indexOf(componentName) === -1
            && Object.values(MODAL_NAMES__KITCHEN).indexOf(componentName) === -1
            && Object.values(MODAL_NAMES__KITCHEN_CUSTOMER).indexOf(componentName) === -1
        ) {
            throw new Error(`componentName '${componentName}' was not found in the MODAL_NAMES list.`);
        }

        // Generate a unique ID for the modal.
        // This allows multiple instances of the same modal to be open at the same time.
        const modalID = this.generateID();

        let transition;
        // Determine a transition based on the component name.
        if (componentName.endsWith('ModalSheet')) {
            // Sheets come from the right on desktop (and the bottom at mobile widths)
            transition = 'sheet-right';
        } else if (componentName === MODAL_NAMES.ACTION_SHEET) {
            // Action sheets come from the bottom.
            transition = 'sheet-bottom';
        } else {
            // Modals appear in the middle of the screen and fade in/out.
            transition = 'fade';
        }

        const promise = new Promise((resolve, reject) => {
            window.rootVueStore.commit('addModalToStack', {
                modalID,
                value: {
                    componentName,
                    props: {
                        ...props,
                        modalID,
                        resolve: (result) => {
                            this.close(modalID);
                            resolve(result);
                        },
                        reject: (err) => {
                            this.close(modalID);
                            reject(err);
                        },
                    },
                    transition,
                },
            });
        });

        if (options?.onOpen) {
            options.onOpen(modalID);
        }

        return promise;
    }

    /**
     * Opens a modal and handles the 'then' by doing nothing with the result.
     * If the modal promise rejects, the error will be suppressed if it is a ModalCancelledError,
     * otherwise it will continue to reject because it is a real error.
     *
     * @param {string} componentName
     * @param {object} [props]
     * @param {ModalOptions} options
     */
    openAndIgnoreResult(componentName, props = {}, options = {}) {
        this.open(componentName, props, options)
            .then(() => {})
            .catch(suppressModalCancelledErrorOrReThrow);
    }

    close(modalID) {
        window.rootVueStore.commit('removeModalFromStack', modalID);
    }

    /**
     * Closes all instances of a modal type.
     *
     * @param {string} componentName
     */
    closeAllOfType(componentName) {
        const activeModals = this.getActiveModals();
        Object.entries(activeModals).forEach(([modalID, modal]) => {
            if (modal.componentName === componentName) {
                this.close(modalID);
            }
        });
    }

    generateID() {
        return (new Date()).getTime();
    }

    getActiveModals() {
        return window.rootVueStore.state.activeModals;
    }

    /**
     * @return {number}
     */
    countActiveModals() {
        return Object.keys(this.getActiveModals()).length;
    }
}
